import { Fragment } from "react/jsx-runtime";
import { useMemo } from "react";
import { CompanyTreeView } from "../Selector/CompanySelector/CompanyTreeView";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { CompanyMandatoryFields } from "src/services/Companies/CompaniesService";
import { useSwitchCompany } from "src/services/Companies/useSwitchCompany";
import { Box, Typography } from "@mui/material";
import { FoodsIcon } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useOrganizationHierarchy } from "src/api/client-api/company/useOrganizationHierarchy";
import { Company } from "src/api/client-api/company/organizationSchema";

type CompanySelectorSidebarProps = {
    userCompanies: CompanyMandatoryFields[];
};
export const CompanySelectorSidebar = (props: CompanySelectorSidebarProps) => {
    const { selectedCompany } = useFoodpilotContext();
    const { t } = useTranslation();
    const { changeCompany } = useSwitchCompany();
    const { rootCompanies, isLoading } = useOrganizationHierarchy();

    const selectableIds = useMemo(
        () => new Set(props.userCompanies.map((company) => company.id)),
        [props.userCompanies],
    );

    const filteredRootCompanies = useMemo(
        () => filterAuthorizedTree(rootCompanies, selectableIds),
        [rootCompanies, selectableIds],
    );

    if (selectedCompany === null) return;
    return (
        <Fragment>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "8px",

                    justifyContent: "left",
                    alignItems: "center",
                }}
            >
                <FoodsIcon size={4} icon="organization" />
                <Typography variant="h4">{t("sidebar.selectorganization")}</Typography>
            </Box>
            <CompanyTreeView
                companies={filteredRootCompanies}
                isLoading={isLoading}
                selectedCompany={selectedCompany}
                selectableIds={selectableIds}
                onSelect={(newCompany) => {
                    changeCompany(newCompany.id);
                }}
            />
        </Fragment>
    );
};

const filterAuthorizedTree = (companies: Company[] | undefined, allowedIds: Set<number>): Company[] => {
    if (!companies || companies.length === 0) {
        return [];
    }

    const result: Company[] = [];

    for (const company of companies) {
        if (allowedIds.has(company.id)) {
            const filteredChildren = filterAuthorizedTree(company.children, allowedIds);
            result.push({ ...company, children: filteredChildren });
        } else {
            const filteredChildren = filterAuthorizedTree(company.children, allowedIds);
            if (filteredChildren.length > 0) {
                result.push({ ...company, children: filteredChildren });
            }
        }
    }

    return result;
};
